import { join } from 'node:path';
import { homedir } from 'node:os';
import { getFlagDefinition } from '@augmented/core';
import {
  defaultFlagsCachePath,
  flagsCacheAgeSeconds,
  readFlagsCache,
  resolveAllFlags,
  resolveFlagFromLayers,
  type ResolvedFlag,
} from '../lib/feature-flags-host.js';
import { error, info, table } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';

interface FlagsResolveOptions {
  flag?: string;
  host?: boolean;
  configDir?: string;
}

/**
 * `agt flags resolve` — debug command (ADR-0022 §5) printing each flag's
 * effective value and the layer it came from (env / heartbeat-cache / compiled
 * default), reading the same last-known-good cache the manager maintains. Pure
 * read: it never contacts the API, so it reflects exactly what a running
 * manager would resolve from disk + env right now.
 */
export function flagsResolveCommand(opts: FlagsResolveOptions): void {
  const json = isJsonMode();
  const configDir = opts.configDir ?? join(homedir(), '.augmented');
  const cachePath = defaultFlagsCachePath(configDir);
  const cache = readFlagsCache(cachePath);
  const heartbeatFlags = cache?.flags;

  let resolved: ResolvedFlag[];
  if (opts.flag) {
    const definition = getFlagDefinition(opts.flag);
    if (!definition) {
      if (json) {
        jsonOutput({ ok: false, error: `Unknown flag '${opts.flag}'` });
      } else {
        error(`Unknown flag '${opts.flag}'. Run \`agt flags resolve\` to list registered flags.`);
      }
      process.exitCode = 1;
      return;
    }
    resolved = [resolveFlagFromLayers(definition, heartbeatFlags, process.env)];
  } else {
    resolved = resolveAllFlags(heartbeatFlags, process.env);
  }

  const ageSeconds = cache ? flagsCacheAgeSeconds(cache, cachePath) : null;

  if (json) {
    jsonOutput({
      ok: true,
      cache: {
        path: cachePath,
        present: cache !== null,
        schema_version: cache?.schema_version ?? null,
        age_seconds: ageSeconds === null ? null : Math.round(ageSeconds),
      },
      flags: resolved.map((r) => ({
        key: r.key,
        value: r.value,
        source: r.source,
        env_var: r.envVar ?? null,
        env_masks_heartbeat: r.envMasksHeartbeat ?? false,
      })),
    });
    return;
  }

  info('Feature flags — resolved env -> heartbeat-cache -> compiled default');
  if (cache === null) {
    info(`Cache: ${cachePath} (absent — manager hasn't cached a heartbeat yet; showing env + compiled defaults)`);
  } else {
    const age = ageSeconds === null ? 'unknown' : `${Math.round(ageSeconds)}s`;
    info(`Cache: ${cachePath} (age: ${age}, schema: ${cache.schema_version || 'unknown'})`);
  }

  table(
    ['KEY', 'VALUE', 'SOURCE', 'NOTE'],
    resolved.map((r) => {
      let note = '';
      if (r.envMasksHeartbeat) {
        note = `env ${r.envVar} masks heartbeat value`;
      } else if (r.source === 'env') {
        note = `via ${r.envVar}`;
      }
      return [r.key, String(r.value), r.source, note];
    }),
  );

  // ENG-6251: surface cache keys this CLI doesn't recognise (registry skew with
  // a newer API). They're stored verbatim and inert — shown only as a hint that
  // a CLI upgrade may unlock new flags.
  if (opts.host && cache) {
    const unknown = Object.keys(cache.flags).filter((key) => !getFlagDefinition(key));
    if (unknown.length > 0) {
      info(`Unrecognised cached keys (inert; upgrade agt-cli to consume): ${unknown.join(', ')}`);
    }
  }
}
